import { defineStore } from 'pinia';
import { DefaultPaginationItems, type PaginationItems } from '@/models/pagination';
import { DefaultModal, type Modal } from '@/models/modal';
import { DefaultFilterRequest } from '@/models/filter';
import type { TransportVehicleListModel, TransportVehicleListRequest } from '@/models/transport-vehicle/transport-vehicle';
import { listTransportVehicles } from '@/services/transport-vehicle/transport-vehicle-service';
import { assignTrajectories } from '@/services/trajectory/trajectory-service';
import {
    cancelPlanRun,
    checkPlanRun,
    getActivePlanRun,
    getPlanRunSummary,
    startPlanRun,
} from '@/services/plan-run/plan-run-service';
import { resilientStorage } from '@/helpers/safe-storage';
import { getDefaultStartDate } from '@/helpers/default-start-date-helper';
import { useOrganizationStore } from '@/stores/organization/organization-store';
import { useAuthStore } from '@/stores/authentication-store';
import { CancelPlanRunOutcome, type CancelPlanRunResponse, type PlanRun, type PlanRunCheck, type PlanRunSummary } from '@/models/plan-run/plan-run';
import { PlanRunPhase } from '@/constants/plan-run-phase';
import { PlanRunStatus } from '@/constants/plan-run-status';
import { PlanRunFailureProfile } from '@/constants/plan-failure-reason';

// Eigen lijst-state (niet die van de transportvoertuigen-store): deze modal zet een eigen take en
// een plannedOn op de gekozen startdatum, en mag de voertuigenlijstpagina + de handmatige
// transport-modal daarmee niet overschrijven.
//
// take -1 = alle actieve transportvoertuigen in één keer (backend-conventie). De vloot is klein genoeg
// om in één lijst te tonen; daardoor kan de modal zonder paginering en sorteert de tabel client-side
// over de complete selectie in plaats van over één pagina.
const defaultVehicleListRequest = (): TransportVehicleListRequest => ({
    page: 1,
    take: -1,
    filterOptions: DefaultFilterRequest(),
    isActive: true,
});

interface PlanRunState {
    modal: Modal;
    /** Startmoment van de planrun: gekozen datum + de standaard starttijd uit de organisatie-instellingen. */
    startDate: number;
    vehicleListRequest: TransportVehicleListRequest;
    transportVehicles: PaginationItems<TransportVehicleListModel>;
    selectedVehicleIds: number[];
    trajectoryIds: number[];
    /**
     * De meest recente run van de organisatie. Ook een afgeronde run blijft staan: alleen zo kan de
     * dagplanner een geslaagde van een mislukte run onderscheiden nadat die niet meer actief is.
     */
    latestRun: PlanRun | null;
    /** latestRun is Pending of Running. */
    latestRunIsActive: boolean;
    /** False tot de eerste geslaagde opvraging, zodat "nog niets opgehaald" niet als "geen run" leest. */
    latestRunLoaded: boolean;
    /** Servertijd minus browsertijd bij de laatste opvraging, voor een voortgangsweergave zonder klokdrift. */
    serverTimeOffset: number;
    /**
     * De run waarvan de planner de voortgangsweergave heeft weggeklikt. Per run-id, zodat een volgende run
     * de weergave weer laat zien. Staat in de store en niet in het paneel zelf: de weergave hangt app-breed,
     * dus wegklikken moet een routewissel overleven.
     */
    dismissedRunId: number | null;
    /** De samenvatting van de afgelopen run, of null zolang die niet opgehaald is. */
    summary: PlanRunSummary | null;
    summaryLoading: boolean;
    /** Van welke run de samenvatting-dialoog openstaat; null = dicht. */
    summaryRunId: number | null;
    /** De uitkomst van de controle vóór verzenden, of null zolang er niet gecontroleerd is. */
    check: PlanRunCheck | null;
    checkLoading: boolean;
    /**
     * Er loopt een afbreekverzoek. Zolang dit staat is de knop bezet: het verzoek reist naar de planning
     * engine en dat duurt even, en twee keer drukken zou de tweede aanroep op een al afgebroken run laten
     * landen — technisch onschuldig, maar de planner zou een "was al klaar"-melding krijgen op zijn eigen
     * eerste klik.
     */
    cancelling: boolean;
}

/**
 * Runs waarvan de planner de afloop gezien heeft, per gebruiker bewaard.
 *
 * Nodig omdat de dialoog anders twee kanten op fout gaat: verversen op het verkeerde moment laat hem opnieuw
 * opkomen, en verversen ná de afronding zou hem helemaal overslaan. Met een bevestiging per run komt hij
 * precies één keer, ook als de planner tussendoor de pagina herlaadt.
 */
const ACKNOWLEDGED_RUNS_KEY = 'plan-run.acknowledged';
/** Ruim genoeg voor een werkdag aan runs; voorkomt dat de sleutel eindeloos groeit. */
const MAX_ACKNOWLEDGED_RUNS = 50;

const acknowledgedKeyFor = (userId: string): string => `${ACKNOWLEDGED_RUNS_KEY}.${userId}`;

const readAcknowledgedRuns = (userId: string): number[] => {
    const raw = resilientStorage.getItem(acknowledgedKeyFor(userId));
    if (!raw) {
        return [];
    }

    try {
        const parsed: unknown = JSON.parse(raw);

        return Array.isArray(parsed) ? parsed.filter((x): x is number => typeof x === 'number') : [];
    } catch {
        // Onleesbare opslag mag de dialoog niet blokkeren; dan liever één keer te veel dan nooit meer.
        return [];
    }
};

export const usePlanRunStore = defineStore({
    id: 'plan-run',
    state: () => ({
        modal: DefaultModal(),
        startDate: getDefaultStartDate(),
        vehicleListRequest: defaultVehicleListRequest(),
        transportVehicles: DefaultPaginationItems(),
        selectedVehicleIds: [],
        trajectoryIds: [],
        latestRun: null,
        latestRunIsActive: false,
        latestRunLoaded: false,
        serverTimeOffset: 0,
        dismissedRunId: null,
        summary: null,
        summaryLoading: false,
        summaryRunId: null,
        check: null,
        checkLoading: false,
        cancelling: false,
    } as PlanRunState),
    getters: {
        selectedVehicles(state): TransportVehicleListModel[] {
            return state.transportVehicles.items.filter((vehicle) => state.selectedVehicleIds.includes(vehicle.id));
        },
        /** Er loopt een automatische planning: placeholders tonen en automatisch plannen op slot. */
        isPlanRunActive(state): boolean {
            return state.latestRunIsActive;
        },
        activeRun(state): PlanRun | null {
            return state.latestRunIsActive ? state.latestRun : null;
        },
        /**
         * De lopende run is zijn resultaat al aan het verwerken tot concept-transporten. Afbreken kan dan
         * niet meer (de server weigert het met ResultBeingApplied); de knop hoort dat vóór te zijn.
         */
        activeRunIsApplying(): boolean {
            return this.activeRun?.phase === PlanRunPhase.Applying;
        },
        /**
         * Middernacht (lokaal) van de dag waarop de lopende run plant, of null. De dagplanner vergelijkt
         * hiermee; de normalisatie staat bewust op één plek.
         */
        activeRunStartDay(): number | null {
            const run = this.activeRun;

            return run ? new Date(run.startDate).setHours(0, 0, 0, 0) : null;
        },
        activeRunVehicleIds(): Set<number> {
            return new Set(this.activeRun?.transportVehicleIds ?? []);
        },
        /**
         * Wat op dit moment vergrendeld is doordat er een plan-run loopt (werkitem 7333).
         *
         * Leeg zodra er geen run actief is, dus ontgrendelen is hier net zo goed geen handeling als aan de
         * serverkant. Deze verzamelingen zijn uitsluitend voor de weergave: de server weigert het opslaan
         * ongeacht wat de lijst denkt, want de poller loopt tot twintig seconden achter.
         */
        lockedTrajectoryIds(): Set<number> {
            return new Set(this.activeRun?.trajectoryIds ?? []);
        },
        lockedAssignmentIds(): Set<number> {
            return new Set(this.activeRun?.assignmentIds ?? []);
        },
        lockedTransportVehicleIds(): Set<number> {
            return new Set(this.activeRun?.transportVehicleIds ?? []);
        },
        lockedVehicleIds(): Set<number> {
            return new Set(this.activeRun?.vehicleIds ?? []);
        },
        /**
         * Relaties die als standplaats van een deelnemende vrachtwagen dienen.
         *
         * Alléén het bezoekadres en het deactiveren staan daarop op slot: de rest van de relatie raakt de
         * berekening niet. Elke andere relatie in de run — laad- en losadressen — blijft volledig bewerkbaar,
         * want een adres dat in gebruik is wordt bij wijzigen gedupliceerd.
         */
        lockedStationRelationIds(): Set<number> {
            return new Set(this.activeRun?.stationRelationIds ?? []);
        },
        /**
         * Mag deze gebruiker zien dát er automatisch gepland wordt?
         *
         * Dezelfde claim als PlanRun/Start, en die hebben alleen de planner, de organisatiebeheerder en de
         * administrator. Bewust op de claim en niet op de rol: authStore.role houdt alleen de eerste rol van
         * de gebruiker vast, terwijl claims over alle rollen samengevoegd worden. Let op dat een alleen-lezen
         * gebruiker wél transport.read heeft en het leesendpoint dus mag aanroepen — zonder deze getter zou
         * die de voortgang gewoon zien.
         */
        canSeePlanRun(): boolean {
            return useAuthStore().hasClaims(['trajectory.update']);
        },
        /**
         * Moet de stand opgehaald worden voor deze gebruiker?
         *
         * Ruimer dan {@link canSeePlanRun}, en dat verschil is de kern: de vergrendeling raakt ook opdrachten,
         * voertuigen en transportvoertuigen. Wie die wél mag bewerken maar geen trajectory.update heeft, zou
         * zonder deze getter helemaal geen stand ophalen — en dan is elk slot in de weergave onzichtbaar en
         * krijgt hij bij het opslaan een weigering die uit de lucht komt vallen.
         *
         * hasAtLeastOneClaim en niet hasClaims: één van deze rechten is genoeg om ergens tegen een slot aan te
         * kunnen lopen.
         *
         * Deze lijst moet meegroeien met de guards. Twee stonden er niet in terwijl het slot ze wél raakt: het
         * bezoekadres van een standplaats (relation.update) en de planningsinstellingen
         * (organization-settings.update). Beide schermen tonen een staande melding, en zonder de stand blijft
         * die melding weg terwijl het opslaan wél geweigerd wordt.
         *
         * De transport.read-voorwaarde is geen inhoudelijke keuze maar een randvoorwaarde: PlanRun/GetActive
         * eist die claim, en wie hem mist krijgt op elke poll een 401 — en de httpClient behandelt elke 401
         * als een verlopen sessie, met een stille her-login (en mogelijk een uitlog) per poll als gevolg.
         * Concreet raakt dit de externe opdrachtgever: die heeft relation.update maar geen transport.read,
         * en valt dus terug op de server-side weigering zonder staande melding — hetzelfde gedrag als vóór
         * deze feature. Wil die de vergrendeling ooit realtime zien, dan moet het endpoint erbij verruimd
         * worden; deze lijst en het bereik van GetActive horen in de pas te blijven.
         */
        needsPlanRunLock(): boolean {
            const authStore = useAuthStore();

            return authStore.hasClaims(['transport.read']) && authStore.hasAtLeastOneClaim([
                'trajectory.update',
                'assignment.update',
                'transport.update',
                'transport-vehicle.update',
                'vehicle.update',
                'relation.update',
                'organization-settings.update',
            ]);
        },
        /** De voortgangsweergave hoort open te staan: er loopt een run en die is niet geminimaliseerd. */
        showProgressPanel(state): boolean {
            return this.canSeePlanRun && this.activeRun !== null && this.activeRun.id !== state.dismissedRunId;
        },
        /**
         * De voortgangsweergave staat samengevouwen. Bewust nog steeds zichtbaar en niet helemaal weg:
         * minimaliseren mag de run niet uit het zicht laten verdwijnen, want dan is er geen weg terug naar de
         * voortgang — en naar de afbreek-knop evenmin.
         */
        showMinimizedProgress(state): boolean {
            return this.canSeePlanRun && this.activeRun !== null && this.activeRun.id === state.dismissedRunId;
        },
        /** De samenvatting-dialoog hoort open te staan. */
        showSummary(state): boolean {
            return state.summaryRunId !== null && state.summary !== null;
        },
    },
    actions: {
        async openModal(trajectoryIds: number[]) {
            this.trajectoryIds = trajectoryIds;
            this.selectedVehicleIds = [];
            this.vehicleListRequest = defaultVehicleListRequest();
            this.transportVehicles = DefaultPaginationItems();
            this.modal.visible = true;
            this.modal.loading = true;

            const authStore = useAuthStore();
            const organizationStore = useOrganizationStore();
            await organizationStore.ensureOrganizationSettings(authStore.organizationId);
            const dateSettings = organizationStore.organizationSettings.dateSettings;
            this.startDate = getDefaultStartDate(dateSettings?.defaultStartDate, dateSettings?.defaultTransportStartTime);

            // Loopt er al een run, dan is automatisch plannen op slot; dat moet zichtbaar zijn vóórdat de
            // planner een selectie maakt.
            await this.fetchLatestRun();

            await this.fetchTransportVehicles();
            this.modal.loading = false;
        },
        closeModal() {
            this.modal.visible = false;
            this.modal.loading = false;
            this.selectedVehicleIds = [];
            this.trajectoryIds = [];
            this.transportVehicles = DefaultPaginationItems();
        },
        async fetchTransportVehicles() {
            this.modal.loading = true;

            // plannedOn markeert de voertuigen die op het gekozen startmoment al op een ander transport staan.
            this.vehicleListRequest.plannedOn = this.startDate;

            const response = await listTransportVehicles(this.vehicleListRequest);
            if (response.status === 200) {
                this.transportVehicles = response.data;
                // Selecties op voertuigen die na de herlading niet meer in de lijst staan vervallen.
                const availableIds = new Set(this.transportVehicles.items.map((vehicle) => vehicle.id));
                this.selectedVehicleIds = this.selectedVehicleIds.filter((id) => availableIds.has(id));
            }

            this.modal.loading = false;
        },
        /**
         * Eén geselecteerd voertuig = direct een transport, het bestaande handmatige gedrag: capaciteit en
         * chauffeur komen van het voertuig zelf (in de modal niet instelbaar). Meerdere voertuigen leveren
         * een planrun op — die loopt niet langs deze actie.
         *
         * NB: assignTrajectories stuurt de capaciteit als "assignmentCapacity", terwijl de backend
         * TransportVehicleCapacity verwacht — die blijft dus leeg en de backend valt terug op de capaciteit
         * van het voertuig zelf. Voor deze flow is dat precies de bedoeling.
         */
        async createSingleVehicleTransport() {
            const vehicle = this.selectedVehicles[0];
            if (!vehicle) {
                return null;
            }

            this.modal.loading = true;
            try {
                return await assignTrajectories(
                    0,
                    true,
                    this.trajectoryIds,
                    this.startDate,
                    vehicle.id,
                    vehicle.capacity ?? 0,
                    vehicle.defaultChauffeur?.id ?? '',
                );
            } finally {
                this.modal.loading = false;
            }
        },
        /**
         * Meerdere voertuigen = een planrun: de backend zet een plan-run klaar en de orkestratie laat APE
         * de trajecten over de voertuigen verdelen. Het resultaat wordt daar ook verwerkt tot concept-ritten;
         * deze actie start alleen de run en de dagplanner volgt de afloop via de poller.
         */
        async startPlanRun() {
            this.modal.loading = true;
            try {
                const response = await startPlanRun(this.trajectoryIds, this.selectedVehicleIds, this.startDate);

                // Mislukte de start, dan is de reden meestal een run die net vóór ons begon. De stand hier
                // ophalen en niet in de modal: het pad "doorgaan zonder de probleemtrajecten" loopt hier ook
                // langs, en de weigering moet vertellen wie er bezig is in plaats van te wachten op de poller
                // (die tot twintig seconden achterloopt).
                if (response?.status !== 200) {
                    await this.fetchLatestRun();
                }

                return response;
            } finally {
                this.modal.loading = false;
            }
        },
        /**
         * Haalt de plan-run-stand op.
         *
         * Retourneert `undefined` als de opvraging mislukte — dan blijft de bestaande stand staan. Dat is
         * essentieel voor de poller: de gedeelde abort-signal wordt door `abort("Typing")` vanuit de hele
         * app gekild, en state leegmaken bij een non-200 zou de placeholders bij elke toetsaanslag elders
         * laten wegknipperen. `null` betekent wél echt: deze organisatie heeft nog nooit een run gehad.
         */
        async fetchLatestRun(): Promise<PlanRun | null | undefined> {
            const response = await getActivePlanRun();
            if (response?.status !== 200) {
                return undefined;
            }

            this.serverTimeOffset = response.data.serverTime - Date.now();
            this.latestRun = response.data.planRun;
            this.latestRunIsActive = response.data.isActive;
            this.latestRunLoaded = true;

            return this.latestRun;
        },
        /**
         * Zet de zojuist gestarte run alvast in de state, zodat de placeholders er bij de eerste weergave
         * van de dagplanner staan in plaats van pas na de eerste poll. Start geeft alleen een id terug; de
         * rest komt uit de modal-state, en de eerste poll verzoent het met de server.
         */
        seedActiveRun(id: number) {
            this.latestRun = {
                id: id,
                status: PlanRunStatus.Pending,
                startDate: this.startDate,
                transportVehicleIds: [...this.selectedVehicleIds],
                trajectoryIds: [...this.trajectoryIds],
                // Leeg tot de eerste poll: welke opdrachten bij deze trajecten horen weet alleen de server.
                // Tot die tijd zijn de trajecten en voertuigen al wél vergrendeld in de weergave, en de
                // opdrachtenpagina volgt een paar seconden later. De server weigert intussen gewoon.
                assignmentIds: [],
                vehicleIds: [],
                stationRelationIds: [],
                createdTransportCount: null,
                unplannedTrajectoryIds: [],
                failureProfile: PlanRunFailureProfile.None,
                estimatedLegCount: null,
                totalLegCount: null,
                // De planner startte hem zelf; de eerste poll bevestigt dat vanaf de server.
                startedByMe: true,
                phase: null,
                phaseSince: null,
                queuePosition: null,
                error: null,
                processGone: false,
                startedAt: Date.now(),
                completedAt: null,
                createdByName: null,
                cancelledByMe: false,
                cancelledByName: null,
            };
            this.latestRunIsActive = true;
            this.latestRunLoaded = true;
        },
        /**
         * De voortgangsweergave wegklikken. Alleen de weergave: de run loopt gewoon door. Nadrukkelijk iets
         * anders dan afbreken — zie {@link cancelRun} — en dat verschil is de kern van werkitem 7405: een
         * planner mag nooit per ongeluk een run van minuten stoppen omdat hij het paneel uit de weg wilde.
         */
        dismissProgressPanel() {
            this.dismissedRunId = this.latestRun?.id ?? null;
        },
        /** De samengevouwen weergave weer openklappen. */
        restoreProgressPanel() {
            this.dismissedRunId = null;
        },
        /**
         * De lopende run afbreken.
         *
         * Zet de stand meteen zelf bij op wat de server teruggaf, in plaats van op de volgende poll te wachten:
         * die komt pas seconden later, en tot die tijd zou het paneel met een uitgeschakelde knop blijven staan
         * alsof er niets gebeurd was. Mislukt de aanroep, dan blijft de stand juist ongemoeid — de run loopt
         * dan gewoon door en dat hoort de planner te blijven zien.
         *
         * @returns het antwoord van de server, of null als de aanroep niet slaagde.
         */
        async cancelRun(planRunId: number): Promise<CancelPlanRunResponse | null> {
            if (this.cancelling) {
                return null;
            }

            this.cancelling = true;
            try {
                const response = await cancelPlanRun(planRunId);
                if (response?.status !== 200) {
                    return null;
                }

                // Een geweigerde afbreking (het resultaat wordt al verwerkt) laat de stand met rust: de run
                // is dan nog gewoon actief en de poller volgt hem verder.
                if (response.data.outcome === CancelPlanRunOutcome.ResultBeingApplied) {
                    return response.data;
                }

                // Alleen bijwerken als de stand nog over dezelfde run gaat: een poll die tijdens het afbreken
                // binnenkwam kan er al een volgende hebben neergezet.
                if (this.latestRun?.id === planRunId) {
                    this.latestRun = { ...this.latestRun, status: response.data.status, phase: null };
                    this.latestRunIsActive = false;
                }

                return response.data;
            } finally {
                this.cancelling = false;
            }
        },
        /**
         * Haalt de samenvatting van een run op en zet de dialoog open.
         *
         * Wordt ook zonder dialoog gebruikt: de trajectenlijst leest hier de redenen uit voor de waarschuwing
         * per traject. Vandaar dat het openzetten een aparte keuze is.
         */
        async fetchSummary(planRunId: number, options?: { show?: boolean }): Promise<PlanRunSummary | null> {
            this.summaryLoading = true;
            try {
                const response = await getPlanRunSummary(planRunId);
                if (response?.status !== 200) {
                    return null;
                }

                this.summary = response.data;

                if (options?.show === true) {
                    this.summaryRunId = planRunId;
                }

                return this.summary;
            } finally {
                this.summaryLoading = false;
            }
        },
        /**
         * Legt vast dat deze planner de afloop van deze run gepresenteerd heeft gekregen.
         *
         * Wordt aangeroepen zodra we iets tónen, niet pas als hij het wegklikt. De inhaalslag in de poller
         * kijkt hier namelijk elke ronde naar: zonder deze vastlegging zou dezelfde melding om de twintig
         * seconden opnieuw opkomen en zich opstapelen.
         */
        recordAcknowledged(planRunId: number) {
            const userId = useAuthStore().userId;
            if (!userId) {
                return;
            }

            const acknowledged = readAcknowledgedRuns(userId);
            if (acknowledged.includes(planRunId)) {
                return;
            }

            // Nieuwste vooraan en afkappen: de lijst hoeft alleen recente runs te kennen, want een oude run
            // wordt nooit meer als afloop gepresenteerd.
            const updated = [planRunId, ...acknowledged].slice(0, MAX_ACKNOWLEDGED_RUNS);
            resilientStorage.setItem(acknowledgedKeyFor(userId), JSON.stringify(updated));
        },
        /**
         * De samenvatting sluiten. De redenen blijven in de state staan: de waarschuwing in de trajectenlijst
         * leunt erop, en die hoort te blijven staan tot het traject van Te plannen af is.
         */
        acknowledgeSummary() {
            const runId = this.summaryRunId;
            this.summaryRunId = null;

            if (runId !== null) {
                this.recordAcknowledged(runId);
            }
        },
        /** Of deze planner de afloop van deze run al gezien heeft. */
        hasAcknowledged(planRunId: number): boolean {
            const userId = useAuthStore().userId;

            return userId ? readAcknowledgedRuns(userId).includes(planRunId) : false;
        },
        /**
         * Dezelfde run nog eens: dezelfde trajecten, dezelfde voertuigen, dezelfde dag.
         *
         * De gegevens komen van de mislukte run zelf en niet uit de modal-state, want die is na een mislukking
         * allang leeg — de planner kan inmiddels ergens anders in de app zijn. Lukt het starten weer niet, dan
         * komt de poller met een nieuwe melding; dezelfde weg als bij de eerste poging.
         */
        async retryRun(run: PlanRun) {
            this.trajectoryIds = [...run.trajectoryIds];
            this.selectedVehicleIds = [...run.transportVehicleIds];
            this.startDate = run.startDate;

            const response = await startPlanRun(this.trajectoryIds, this.selectedVehicleIds, this.startDate);
            if (response?.status === 200) {
                this.seedActiveRun(response.data);
            } else {
                // Ook opnieuw proberen kan op een run stuiten die inmiddels van iemand anders is; dan hoort de
                // stand direct te kloppen zodat de vergrendeling zichtbaar wordt.
                await this.fetchLatestRun();
            }

            return response;
        },
        /**
         * Controleert de voorgenomen run vóór verzenden. Geeft terug of er iets in de weg staat; de modal
         * beslist daarna zelf of hij de aanvraag laat gaan of de planner eerst laat kiezen.
         */
        async runPreflightCheck(): Promise<PlanRunCheck | null> {
            this.checkLoading = true;
            try {
                const response = await checkPlanRun(this.trajectoryIds, this.selectedVehicleIds);
                // Mislukt de controle zelf, dan houden we de run niet tegen: dit is een hulpmiddel om een
                // onbruikbare aanvraag vóór te zijn, geen extra plek waar automatisch plannen op kan stranden.
                this.check = response?.status === 200 ? response.data : null;

                return this.check;
            } finally {
                this.checkLoading = false;
            }
        },
        clearPreflightCheck() {
            this.check = null;
        },
        /**
         * Doorgaan zonder de trajecten die een probleem hebben: de selectie wordt gesneden op wat de controle
         * bruikbaar vond, waarna de gewone start volgt.
         */
        async startPlanRunWithoutProblemTrajectories() {
            const plannable = this.check?.plannableTrajectoryIds ?? [];
            if (plannable.length === 0) {
                return null;
            }

            this.trajectoryIds = [...plannable];
            this.check = null;

            return await this.startPlanRun();
        },
        setStartDate(startDate: number | null) {
            if (startDate == null) {
                return;
            }

            // De datumkiezer levert middernacht; de standaard starttijd uit de organisatie-instellingen
            // blijft daarmee behouden voor de planrun.
            const organizationStore = useOrganizationStore();
            const defaultStart = new Date(
                getDefaultStartDate(undefined, organizationStore.organizationSettings.dateSettings?.defaultTransportStartTime),
            );

            const picked = new Date(startDate);
            picked.setHours(defaultStart.getHours(), defaultStart.getMinutes(), 0, 0);
            this.startDate = picked.getTime();
        },
    },
});
